import { BaseEnvContribution, rafBasedSto } from "@visactor/vrender-core";

import { CanvasWrapEnableWH } from "./canvas-wrap";

const LYNX_CANVAS_BRIDGE_ERROR = "Lynx canvas bridge is unavailable. VRender Lynx env requires envParams.canvasFactory or a Lynx runtime that exposes createCanvasNG/createCanvas/createOffscreenCanvas/krypton canvas APIs.", LYNX_CANVAS_SIZE_ERROR = "Lynx canvas size is unavailable. Pass stage width/height when creating a Lynx stage canvas.";

function isValidCoordinate(value) {
    return "number" == typeof value && Number.isFinite(value);
}

function pickCoordinate(...values) {
    for (let i = 0; i < values.length; i++) if (isValidCoordinate(values[i])) return values[i];
}

function getPrimaryTouch(event) {
    var _a, _b, _c;
    return null !== (_b = null === (_a = null == event ? void 0 : event.changedTouches) || void 0 === _a ? void 0 : _a[0]) && void 0 !== _b ? _b : null === (_c = null == event ? void 0 : event.touches) || void 0 === _c ? void 0 : _c[0];
}

function getLynxEventPoint(event) {
    const touch = getPrimaryTouch(event), x = pickCoordinate(null == event ? void 0 : event.x, null == event ? void 0 : event.offsetX, null == event ? void 0 : event.clientX, null == event ? void 0 : event.pageX, null == touch ? void 0 : touch.x, null == touch ? void 0 : touch.offsetX, null == touch ? void 0 : touch.clientX, null == touch ? void 0 : touch.pageX), y = pickCoordinate(null == event ? void 0 : event.y, null == event ? void 0 : event.offsetY, null == event ? void 0 : event.clientY, null == event ? void 0 : event.pageY, null == touch ? void 0 : touch.y, null == touch ? void 0 : touch.offsetY, null == touch ? void 0 : touch.clientY, null == touch ? void 0 : touch.pageY);
    return null == x || null == y ? null : {
        x: x,
        y: y
    };
}

function getGlobalLynxRuntime() {
    try {
        return "undefined" != typeof lynx ? lynx : void 0;
    } catch (err) {
        return;
    }
}

function getGlobalSystemPixelRatio() {
    try {
        return "undefined" != typeof SystemInfo ? SystemInfo.pixelRatio : void 0;
    } catch (err) {
        return;
    }
}

function getLynxRuntime(params) {
    var _a, _b;
    return null !== (_b = null !== (_a = null == params ? void 0 : params.lynx) && void 0 !== _a ? _a : null == params ? void 0 : params.runtime) && void 0 !== _b ? _b : getGlobalLynxRuntime();
}

function getLynxPixelRatio(params, runtime) {
    var _a, _b, _c, _d, _e;
    return null !== (_e = null !== (_d = null !== (_a = null == params ? void 0 : params.pixelRatio) && void 0 !== _a ? _a : null === (_c = null === (_b = null == runtime ? void 0 : runtime.getSystemInfoSync) || void 0 === _b ? void 0 : _b.call(runtime)) || void 0 === _c ? void 0 : _c.pixelRatio) && void 0 !== _d ? _d : getGlobalSystemPixelRatio()) && void 0 !== _e ? _e : 1;
}

function attachLynxCanvasToView(canvas, id) {
    var _a;
    return null === (_a = null == canvas ? void 0 : canvas.attachToCanvasView) || void 0 === _a || _a.call(canvas, id), 
    canvas;
}

function createBoundLynxCanvas(id, runtime) {
    var _a, _b, _c;
    if ("function" == typeof (null === (_a = null == runtime ? void 0 : runtime.krypton) || void 0 === _a ? void 0 : _a.createCanvas)) {
        const canvas = runtime.krypton.createCanvas(id);
        if (canvas) return canvas;
    }
    if ("function" == typeof (null == runtime ? void 0 : runtime.createCanvasNG)) {
        const canvas = runtime.createCanvasNG(id);
        if (canvas) return attachLynxCanvasToView(canvas, id);
    }
    if ("function" == typeof (null === (_b = null == runtime ? void 0 : runtime.krypton) || void 0 === _b ? void 0 : _b.createCanvasNG)) {
        const canvas = runtime.krypton.createCanvasNG();
        if (canvas) return attachLynxCanvasToView(canvas, id);
    }
    if ("function" == typeof (null === (_c = null == runtime ? void 0 : runtime.krypton) || void 0 === _c ? void 0 : _c.CanvasElement)) return new runtime.krypton.CanvasElement(id);
    if ("function" == typeof (null == runtime ? void 0 : runtime.createCanvas)) {
        const canvas = runtime.createCanvas(id);
        if (canvas) return canvas;
    }
    return null;
}

function getCanvasSize(width, height) {
    if (!isValidCoordinate(width) || !isValidCoordinate(height)) throw new Error(LYNX_CANVAS_SIZE_ERROR);
    return {
        width: width,
        height: height
    };
}

function wrapLynxNativeCanvas(nativeCanvas, id, width, height, dpr) {
    nativeCanvas.width = width * dpr, nativeCanvas.height = height * dpr;
    const ctx = nativeCanvas.getContext("2d");
    return new CanvasWrapEnableWH(nativeCanvas, ctx, dpr, width, height, id);
}

function createLynxNativeCanvas(id, width, height, dpr, offscreen, params, runtime) {
    if (params.canvasFactory) return params.canvasFactory({
        id: id,
        width: width,
        height: height,
        dpr: dpr,
        offscreen: offscreen
    });
    if (offscreen) {
        if ("function" == typeof (null == runtime ? void 0 : runtime.createOffscreenCanvas)) return runtime.createOffscreenCanvas();
    } else {
        const canvas = createBoundLynxCanvas(id, runtime);
        if (canvas) return canvas;
    }
    throw new Error(LYNX_CANVAS_BRIDGE_ERROR);
}

export function createImageElement(src, isSvg = !1, runtime = getGlobalLynxRuntime()) {
    if (isSvg) return Promise.reject();
    if ("function" != typeof (null == runtime ? void 0 : runtime.createImage)) return Promise.reject(new Error("Lynx image bridge is unavailable."));
    const img = runtime.createImage(src);
    return new Promise(((resolve, reject) => {
        img.onload = () => {
            resolve(img);
        }, img.onerror = () => {
            reject(new Error("加载失败"));
        };
    }));
}

export class LynxEnvContribution extends BaseEnvContribution {
    constructor() {
        super(), this.type = "lynx", this.supportEvent = !0, this.canvasMap = new Map, this.freeCanvasList = [], 
        this.canvasIdx = 0, this.supportsTouchEvents = !0;
        try {
            this.supportsPointerEvents = !!globalThis.PointerEvent, this.supportsMouseEvents = !!globalThis.MouseEvent;
        } catch (err) {
            this.supportsPointerEvents = !1, this.supportsMouseEvents = !1;
        }
        this.applyStyles = !0;
    }
    configure(service, params = {}) {
        service.env === this.type && (service.setActiveEnvContribution(this), this.lynxEnvParams = params, 
        this.lynxRuntime = getLynxRuntime(params));
    }
    getDynamicCanvasCount() {
        return this.freeCanvasList.length;
    }
    getStaticCanvasCount() {
        return 9999;
    }
    loadImage(url) {
        return createImageElement(url, !1, this.lynxRuntime).then((img => ({
            data: img,
            loadState: "success"
        }))).catch((() => ({
            data: null,
            loadState: "fail"
        })));
    }
    loadSvg(url) {
        return Promise.resolve({
            data: null,
            loadState: "fail"
        });
    }
    createCanvas(params) {
        var _a, _b, _c, _d;
        if (null != params.id) {
            const id = String(params.id), existing = this.canvasMap.get(id);
            if (existing) return existing;
            const envParams = null !== (_a = this.lynxEnvParams) && void 0 !== _a ? _a : {}, runtime = getLynxRuntime(envParams), dpr = null !== (_b = params.dpr) && void 0 !== _b ? _b : getLynxPixelRatio(envParams, runtime), size = getCanvasSize(params.width, params.height), canvas = wrapLynxNativeCanvas(createLynxNativeCanvas(id, size.width, size.height, dpr, !1, envParams, runtime), id, size.width, size.height, dpr);
            return this.canvasMap.set(id, canvas), canvas;
        }
        const result = this.freeCanvasList[this.canvasIdx] || this.freeCanvasList[this.freeCanvasList.length - 1];
        if (this.canvasIdx++, result) return result;
        const envParams = null !== (_c = this.lynxEnvParams) && void 0 !== _c ? _c : {}, runtime = getLynxRuntime(envParams), dpr = null !== (_d = params.dpr) && void 0 !== _d ? _d : getLynxPixelRatio(envParams, runtime), size = getCanvasSize(params.width, params.height), id = Math.random().toString(), canvas = wrapLynxNativeCanvas(createLynxNativeCanvas(id, size.width, size.height, dpr, !0, envParams, runtime), id, size.width, size.height, dpr);
        return this.canvasMap.set(id, canvas), this.freeCanvasList.push(canvas), canvas;
    }
    createOffscreenCanvas(params) {}
    releaseCanvas(canvas) {}
    getDevicePixelRatio() {
        return getLynxPixelRatio(void 0, this.lynxRuntime);
    }
    getRequestAnimationFrame() {
        return function(callback) {
            return rafBasedSto.call(callback);
        };
    }
    getCancelAnimationFrame() {
        return h => {
            rafBasedSto.clear(h);
        };
    }
    mapToCanvasPoint(event) {
        var _a;
        return null !== (_a = getLynxEventPoint(event)) && void 0 !== _a ? _a : event;
    }
    addEventListener(type, listener, options) {
        return null;
    }
    removeEventListener(type, listener, options) {
        return null;
    }
    dispatchEvent(event) {
        return null;
    }
    getElementById(str) {
        return this.canvasMap.get(str);
    }
    getRootElement() {
        return null;
    }
    getDocument() {
        return null;
    }
    release(...params) {}
}
//# sourceMappingURL=lynx-contribution.js.map
